import { useEffect, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { PRIVACY_VERSION } from "@/pages/Privacy";
import { useForceDark } from "@/hooks/useForceDark";

const BG = "https://images.unsplash.com/photo-1704633785114-52104ce3d626?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxmb290YmFsbCUyMHBpdGNoJTIwYWVyaWFsJTIwZ3Jhc3N8ZW58MHx8fHwxNzg3MDYxNjM2fDA&ixlib=rb-4.1.0&q=85";

export default function Register() {
  useForceDark();
  const { register, siteSettings } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // Wait for the /public/settings fetch before deciding whether to redirect.
  // Otherwise the very first render (siteSettings === null) or the seeded
  // default would kick users off even when self-registration is actually ON.
  if (siteSettings === null) {
    return <div className="min-h-screen bg-[#0A0F1C] text-white/60 font-display tracking-widest flex items-center justify-center">LOADING…</div>;
  }
  if (siteSettings.self_registration_enabled === false) {
    return <Navigate to="/login" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    if (!consent) return toast.error("Please accept the Privacy Notice to continue");
    setBusy(true);
    try {
      const u = await register({ ...form, consent_accepted: true, privacy_version: PRIVACY_VERSION });
      toast.success(`Welcome, ${u.name}`);
      nav("/app/pitches");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white grid md:grid-cols-2">
      <div className="hidden md:block relative overflow-hidden">
        <img src={BG} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0F1C] via-[#0A0F1C]/80 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="Elsenham YFC" className="w-11 h-11 rounded-full object-contain" />
            <div className="font-display font-black text-lg leading-none tracking-tight">ELSENHAM YFC</div>
          </Link>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Join</div>
            <h1 className="font-display font-black text-5xl mt-3 leading-tight">Squad up.<br/>Book smart.</h1>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-start px-6 md:px-16 py-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="register-form">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">New here</div>
            <h2 className="font-display font-black text-4xl mt-2">Create account</h2>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs tracking-widest uppercase text-white/60">Name</Label>
              <Input required value={form.name} onChange={upd("name")} data-testid="register-name-input" className="mt-2 bg-[#111827] border-white/10" />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-white/60">Email</Label>
              <Input type="email" required value={form.email} onChange={upd("email")} data-testid="register-email-input" className="mt-2 bg-[#111827] border-white/10" />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-white/60">Phone (optional)</Label>
              <Input value={form.phone} onChange={upd("phone")} data-testid="register-phone-input" className="mt-2 bg-[#111827] border-white/10" />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-white/60">Password</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={upd("password")} data-testid="register-password-input" className="mt-2 bg-[#111827] border-white/10" />
            </div>
          </div>
          <label className="flex items-start gap-2 text-xs text-white/70 leading-snug">
            <input
              type="checkbox"
              checked={consent}
              onChange={e => setConsent(e.target.checked)}
              data-testid="register-consent-checkbox"
              className="mt-0.5 accent-[#83B68B]"
            />
            <span>
              I've read and accept the{" "}
              <Link to="/privacy" target="_blank" className="text-[#83B68B] hover:underline" data-testid="register-privacy-link">Privacy Notice</Link>
              {" "}(v{PRIVACY_VERSION}).
            </span>
          </label>
          <button type="submit" disabled={busy || !consent} data-testid="register-submit-btn" className="btn-volt w-full py-3 rounded-md disabled:opacity-50">
            {busy ? "Creating…" : "Create account"}
          </button>
          <div className="text-sm text-white/60 text-center">
            Already registered?{" "}
            <Link to="/login" className="text-[#83B68B] hover:underline" data-testid="register-to-login-link">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
